/** Copyright (c) 2018 T.Rowe Price. All rights reserved. */

import './LogoutPage.scss';
import { isEmpty } from 'lodash';
import React, { Fragment, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import LogoutBanner from 'components/pages/Logout/LogoutBanner';
import LogoutAdvertising from 'components/pages/Logout/LogoutAdvertising';
import { getRouter } from 'state/router/RouterSelectors';
import { SecureNavigationBeacon } from '@trp-ii-ii-component-library/ii-components';
import { aamLoginUrl } from 'services/Externals';
import { GLOBALS } from 'services/globals';
import {
  addLRNEnabled,
  brokerageFrameEnabled,
  redirectToClientPortalEnabled
} from 'services/FeatureFlags';
import { legalReferenceNumbers } from 'services/Content';
import LegalReferenceNumber from 'components/layouts/LegalReferenceNumber/LegalReferenceNumber';
import {
  THEME_ALASKA529,
  THEME_DEFAULT,
  THEME_MARYLAND529,
  THEME_NATIONAL529
} from 'state/view/ViewReducer';
import AAMFooterContainer from 'components/layouts/Footer/FooterUtil';
import { getQualtricsData } from 'state/analytics/analyticsSelectors';
import {
  getQueryStringParams,
  removeBrowserCookie,
  setCookie
} from 'support/BrowserUtils';
import { removeSessionStorageItem } from 'support/SessionStorageUtils';
import {
  getProductClass,
  ACTIVE_ADVISORY,
  ACTIVE_PLUS,
  ALASKA_529,
  BROKERAGE,
  isCollegeSavings,
  MARYLAND_529,
  MUTUAL_FUND,
  NATIONAL_529
} from 'services/Products';
import { formatProductsForSharedLib } from 'services/Accounts';
import {has529Only, isMobile} from 'state/view/ViewSelectors';

export const ReturnToHomepage = ({ view }) => {
  const { hasAlaska529Only, hasMaryland529Only, hasNational529Only } = view;

  if (hasAlaska529Only) {
    return (
      <a href={'https://www.alaska529plan.com/index.html'} className="link">
        Return to Homepage
      </a>
    );
  }
  if (hasMaryland529Only) {
    return (
      <a href={'https://maryland529.com/'} className="link">
        Return to Homepage
      </a>
    );
  }
  if (hasNational529Only) {
    return (
      <a
        href={`${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}/personal-investing/troweprice-529/index.html`}
        className="link">
        Return to Homepage
      </a>
    );
  }
  return (
    <a
      href={`${GLOBALS.AAM_UI_EXTERNAL_BASE_URL}/personal-investing/index.html`}
      className="link">
      Return to Homepage
    </a>
  );
};

export const PostLogoutPage = ({ router: { location }, isMobileApp }) => {
  const { search } = location;
  const queryParams = getQueryStringParams(search);
  const productQuery =
    queryParams &&
    queryParams.find((param) => {
      if (param && param['productTypes']) {
        return param['productTypes'];
      }
    });
  const { productTypes } = productQuery || {};

  useEffect(() => {
    removeSessionStorageItem('returningPVLUser');
    removeBrowserCookie('additionalParams');
    if(redirectToClientPortalEnabled()) {
      setCookie('client-portal', true);
      setCookie('aam-dashboard', false);
    } else {
      setCookie('client-portal', false);
    }
  }, [])

  const products = productTypes
    ? productTypes.split(',').map((p) => parseInt(p))
    : [];
  const simulatedAccounts = products.map((productType) => {
    return { productType };
  });

  const hasProduct = (products, productType) =>
    !isEmpty(products) && products.some((pt) => pt === productType);
  const hasOnlyProduct = (products, productType) =>
    !isEmpty(products) && products.every((pt) => pt === productType);

  const simpleView = {
    hasActivePlus: hasProduct(products, ACTIVE_PLUS),
    hasMaryland529: hasProduct(products, MARYLAND_529),
    hasAlaska529: hasProduct(products, ALASKA_529),
    hasNational529: hasProduct(products, NATIONAL_529),
    hasAlaska529Only: hasOnlyProduct(products, ALASKA_529),
    hasBrokerage: hasProduct(products, BROKERAGE),
    hasCollegeSavingsOnly: products.length ? products.every((pt) => isCollegeSavings(pt)) : false,
    hasManaged:
      hasProduct(products, ACTIVE_PLUS) ||
      hasProduct(products, ACTIVE_ADVISORY),
    hasRAS: hasProduct(products, ACTIVE_ADVISORY),
    hasMaryland529Only: hasOnlyProduct(products, MARYLAND_529),
    hasMutualFund: hasProduct(products, MUTUAL_FUND),
    hasNational529Only: hasOnlyProduct(products, NATIONAL_529)
  };

  const footerProductTypes = formatProductsForSharedLib(simpleView);

  const getTheme = (view) => {
    const { hasAlaska529Only, hasMaryland529Only, hasNational529Only } = view;
    return hasAlaska529Only
      ? THEME_ALASKA529
      : hasMaryland529Only
      ? THEME_MARYLAND529
      : hasNational529Only
      ? THEME_NATIONAL529
      : THEME_DEFAULT;
  };

  const theme = getTheme(simpleView);

  return (
    <Fragment>
    <SecureNavigationBeacon
        authentication={{
          authorization: {
            isCustomerWebView: false
          },
          loginUrl: aamLoginUrl(getProductClass(simpleView)),
          user: {}
        }}
        data={{
          accounts: simulatedAccounts,
          brokerageTransactionsEnabled: brokerageFrameEnabled(),
          showMainMenu: false,
          showContextualNav: true,
          showHelp: true
        }}
      />
      <div className={`post-logout-page ${theme}`}>
        <LogoutBanner
          title="You have successfully logged out."
          body={
            <div className="post-logout-body">
              <div className="post-logout-message">
                To protect the privacy of your information, we recommend that
                you close your browser. Until your browser is closed, the
                browser memory could contain information about your account(s)
                viewed during the session.
              </div>
              <ReturnToHomepage view={simpleView} />
            </div>
          }
        />
        <LogoutAdvertising view={simpleView} />
      </div>
      {addLRNEnabled() && (
        <div>
          <LegalReferenceNumber
            providedLegalNumber={legalReferenceNumbers.LOGOUT_PAGE_LRN}
          />
        </div>
      )}
      <AAMFooterContainer
        footerProductTypes={footerProductTypes}
        isMobileParam={isMobileApp}
        appLoaded={true}
        showOldFooter={simpleView.hasCollegeSavingsOnly}
      />
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  router: getRouter(state),
  isMobileApp: isMobile(state),
  qualtricsData: getQualtricsData(state),
  has529Only: has529Only(state)
});

export default withRouter(connect(mapStateToProps)(PostLogoutPage));
